/**
 * V51 — Foreign/NRI preferred-demo-time picker with live IST conversion.
 *
 * Renders ONLY inside the "Live Online (Foreign / NRI)" tuition-mode branch
 * of the registration form. The standard India home-tuition form is untouched.
 *
 * Props:
 *   value: { date, time24, tz, country_code, country_name }
 *   onChange: (next) => void   — partial updates merged at parent
 */
import { useMemo, useRef } from "react";
import { Globe2, Clock, MousePointerClick } from "lucide-react";
import {
    COUNTRY_TIMEZONES, flagEmoji, getUtcOffset, getTzAbbreviation, convertTime, to12Hour, countTimezonesForCountry,
} from "../lib/timezones";

const IST_TZ = "Asia/Kolkata";

export default function ForeignDemoTimePicker({ value, onChange, autoFilled = false }) {
    const {
        date = "", time24 = "", tz = "", country_code = "", country_name = "",
    } = value || {};

    // Pre-compute UTC offset + abbreviation for the currently-selected tz
    const selectedMeta = useMemo(() => {
        if (!tz) return null;
        const ref = new Date();
        return {
            offset: getUtcOffset(tz, ref),
            abbr: getTzAbbreviation(tz, ref, ""),
        };
    }, [tz]);

    // V129 — Show the "Pick your specific sub-zone" nudge chip only when the
    // auto-detected country has 2+ IANA sub-zones in COUNTRY_TIMEZONES
    // (US:6, AU:4, CA:3). Never renders for single-TZ countries so we don't
    // clutter the form for parents in SG / AE / GB / JP / DE.
    const selectRef = useRef(null);
    const subZoneCount = useMemo(() => countTimezonesForCountry(country_code), [country_code]);
    const shouldNudgeSubZone = autoFilled && subZoneCount >= 2;
    const openSubZonePicker = () => {
        const el = selectRef.current;
        if (!el) return;
        el.focus();
        // Chromium honours a plain click on <select> as "open the popover".
        // Firefox needs a keyboard nudge (Alt+Down) so both paths run.
        try { el.click(); } catch { /* no-op */ }
        try {
            const ev = new KeyboardEvent("keydown", { key: "ArrowDown", altKey: true, bubbles: true });
            el.dispatchEvent(ev);
        } catch { /* no-op */ }
    };

    // V129 — Extract just the base country name for the nudge-chip label,
    // stripping the sub-zone descriptor. "USA — New York / Eastern" → "USA",
    // "Canada — Toronto / Eastern" → "Canada", "Australia (Sydney / Melbourne)"
    // → "Australia". Falls back to the ISO if `country_name` is empty.
    const baseCountryLabel = useMemo(() => {
        const raw = (country_name || "").trim();
        if (!raw) return country_code || "";
        return raw
            .replace(/\s*[—–-]\s.*$/, "")     // strip "— sub-zone / detail"
            .replace(/\s*\(.*\)\s*$/, "")     // strip "(Sydney / Melbourne)"
            .trim() || raw;
    }, [country_name, country_code]);

    // Live IST conversion (DST-aware via convertTime)
    const istConversion = useMemo(() => {
        if (!date || !time24 || !tz) return null;
        return convertTime(date, time24, tz, IST_TZ);
    }, [date, time24, tz]);

    const handleCountryChange = (e) => {
        const idx = parseInt(e.target.value, 10);
        if (Number.isNaN(idx) || idx < 0) {
            onChange({ tz: "", country_code: "", country_name: "" });
            return;
        }
        const entry = COUNTRY_TIMEZONES[idx];
        onChange({ tz: entry.tz, country_code: entry.code, country_name: entry.name });
    };

    const selectedIdx = useMemo(() => {
        if (!tz) return -1;
        return COUNTRY_TIMEZONES.findIndex((c) => c.tz === tz && c.name === country_name);
    }, [tz, country_name]);

    // V125 — Minimum bookable date is TOMORROW (device-local calendar).
    // Anand Tutorials needs 24–48 hours to arrange a new demo, so same-day
    // bookings are blocked in the UI + independently on the backend (which
    // enforces "IST tomorrow" so no timezone workaround can bypass it).
    const tomorrow = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${dd}`;
    }, []);

    const localPreview = date && time24 ? `${flagEmoji(country_code)} ${country_name} (${selectedMeta?.abbr || ""}) → ${to12Hour(time24)} on ${date}` : "";

    return (
        <div className="space-y-3" data-testid="foreign-demo-time-picker">
            <div className="grid sm:grid-cols-2 gap-3">
                {/* Country + timezone */}
                <label className="block sm:col-span-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5 block">
                        Country & Timezone *
                    </span>
                    <div className="relative">
                        <Globe2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <select
                            ref={selectRef}
                            value={selectedIdx >= 0 ? selectedIdx : ""}
                            onChange={handleCountryChange}
                            data-testid="foreign-tz-country"
                            className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                        >
                            <option value="">Select country / region…</option>
                            {COUNTRY_TIMEZONES.map((c, i) => (
                                <option key={`${c.code}-${c.tz}-${i}`} value={i}>
                                    {flagEmoji(c.code)} {c.name} ({c.abbrFallback})
                                </option>
                            ))}
                        </select>
                    </div>
                    {selectedMeta && (
                        <div className="text-[11px] text-slate-500 mt-1">
                            {flagEmoji(country_code)} {country_name}
                            {selectedMeta.abbr ? ` — ${selectedMeta.abbr}` : ""} ({selectedMeta.offset})
                        </div>
                    )}
                    {autoFilled && selectedMeta && (
                        <p
                            className="mt-1.5 text-[11px] text-emerald-700"
                            data-testid="foreign-tz-auto-hint"
                        >
                            Timezone auto-detected from phone code: <strong>{country_name}</strong> · <strong>{selectedMeta.abbr || selectedMeta.offset}</strong>
                        </p>
                    )}
                    {/* V129 — Sub-zone nudge chip for multi-TZ countries
                        (US:6, AU:4, CA:3). Helps Denver / Perth / Vancouver
                        families spot the specific option without re-hunting.
                        Hidden as soon as the parent picks a sub-zone (which
                        clears `_tz_autofill` via the parent form, flipping
                        `autoFilled` to false). */}
                    {shouldNudgeSubZone && (
                        <button
                            type="button"
                            onClick={openSubZonePicker}
                            data-testid="foreign-tz-subzone-nudge"
                            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 hover:bg-amber-100 active:bg-amber-200 border border-amber-300 px-3 py-1.5 text-[11px] font-semibold text-amber-900 transition-colors"
                            aria-label={`Pick your specific ${country_name} sub-zone`}
                        >
                            <MousePointerClick className="w-3 h-3" />
                            {subZoneCount} sub-zones in {baseCountryLabel} — pick yours
                            <span aria-hidden="true">→</span>
                        </button>
                    )}
                </label>

                {/* Date */}
                <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5 block">
                        Preferred Date *
                    </span>
                    <input
                        type="date"
                        value={date}
                        min={tomorrow}
                        onChange={(e) => onChange({ date: e.target.value })}
                        data-testid="foreign-tz-date"
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                    />
                    {/* V125 — 24–48hr lead-time notice (mandatory copy, do not shorten) */}
                    <p
                        className="mt-1.5 text-[11px] leading-snug text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2"
                        data-testid="foreign-tz-date-note"
                    >
                        <strong className="text-amber-900">Please note:</strong> New demo classes generally require 24–48 hours to arrange. Please select a date from tomorrow onward. Our team will confirm the exact demo time with you after reviewing your request.
                    </p>
                </label>

                {/* Time (24h native input, displayed as 12h alongside) */}
                <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5 block">
                        Preferred Time (your local) *
                    </span>
                    <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                            type="time"
                            value={time24}
                            onChange={(e) => onChange({ time24: e.target.value })}
                            data-testid="foreign-tz-time"
                            className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                        />
                    </div>
                    {time24 && (
                        <div className="text-[11px] text-slate-500 mt-1" data-testid="foreign-tz-time-12h">
                            12-hour: <strong>{to12Hour(time24)}</strong>
                        </div>
                    )}
                </label>
            </div>

            {/* Live IST conversion banner */}
            {istConversion && (
                <div
                    className="rounded-xl border-2 border-[#1E3A8A] bg-blue-50 px-4 py-3"
                    data-testid="foreign-tz-ist-banner"
                >
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#1E3A8A] mb-1">
                        Live IST Conversion
                    </div>
                    <div className="text-sm text-slate-700 leading-snug">
                        {localPreview && (
                            <div data-testid="foreign-tz-local-preview" className="mb-0.5">{localPreview}</div>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-[#0F172A]">
                                {flagEmoji("IN")} Indian Standard Time (IST) →
                            </span>
                            <span
                                className="font-bold text-[#1E3A8A] text-base"
                                data-testid="foreign-tz-ist-formatted"
                            >
                                {istConversion.formatted}
                            </span>
                            <span className="text-[11px] text-slate-500">({istConversion.offset})</span>
                        </div>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 italic">
                        Your selected demo time corresponds to <strong>{istConversion.formatted}</strong> Indian Standard Time. Daylight Saving Time is handled automatically.
                    </p>
                </div>
            )}
        </div>
    );
}
